import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import React, { useEffect, useRef } from 'react'
import {
  type FieldError,
  type FieldErrors,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormUnregister,
} from 'react-hook-form'

import { validatePOBoxAddressLine1, validatePOBoxAddressLine2 } from 'utils'

import { FormTextField } from './FormFields'
import { getAbbreviation, StateDropdown } from './StateDropdown'

export type AddressFieldsType = {
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
} & FieldValues

type AddressFieldsProps<T extends AddressFieldsType> = {
  accessToken: string;
  disabled?: boolean;
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
  register: UseFormRegister<T>;
  unregister: UseFormUnregister<T>;
  extraField?: React.ReactElement;
  errors: FieldErrors<T>;
  requiredFields?: Array<keyof T>;
  validatePOBox?: boolean;
}

type ContextItem = {
  id: string;
  text: string;
}

export function AddressFields<T extends AddressFieldsType> ({
  accessToken,
  disabled,
  getValues,
  setValue,
  register,
  unregister,
  extraField,
  errors,
  requiredFields,
  validatePOBox = false,
}: AddressFieldsProps<T>) {
  const addressLine1Ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    register('address_line1' as Path<T>, {
      required: requiredFields?.includes('address_line1')
        ? 'Address line 1 is required.'
        : undefined,
      validate: (value) => validatePOBoxAddressLine1(value, !validatePOBox),
    })

    return () => {
      unregister('address_line1' as Path<T>)
    }
  }, [register, unregister, validatePOBox])

  useEffect(() => {
    const geocoder = new MapboxGeocoder({
      accessToken,
      autocomplete: true,
      countries: 'us',
      minLength: 2,
      placeholder: 'Enter an address',
      types: 'address',
    })
    geocoder.addTo(addressLine1Ref.current as HTMLDivElement)

    if (addressLine1Ref.current) {
      // Since addressLine1Ref is not an input element,
      // we have to explicitly set the value from address_line1 form value
      const inputElement = addressLine1Ref.current.querySelector('input')
      if (inputElement) {
        inputElement.value = getValues('address_line1' as Path<T>)
      }
    }

    geocoder.on('result', (event) => {
      const { place_name: placeName, context } = event.result
      const addressLine1 = placeName.split(',')[0]
      const city = context.find((c: ContextItem) =>
        c.id.startsWith('place')
      ).text
      const state = context.find((c: ContextItem) =>
        c.id.startsWith('region')
      ).text
      const zip = context.find((c: ContextItem) =>
        c.id.startsWith('postcode')
      ).text
      setValue('address_line1' as Path<T>, addressLine1)
      setValue('city' as Path<T>, city)
      setValue(
        'state' as Path<T>,
        getAbbreviation(state) as PathValue<T, Path<T>>
      )
      setValue('zip' as Path<T>, zip)

      if (addressLine1Ref.current) {
        // Since addressLine1Ref is not an input element,
        // we have to explicitly set the value from address_line1 form value
        const inputElement = addressLine1Ref.current.querySelector('input')
        if (inputElement) {
          inputElement.value = addressLine1
        }
      }
    })
    return () => {
      geocoder.onRemove()
    }
  }, [accessToken, getValues, setValue])

  const address2Validation = requiredFields?.includes('address_line2')
    ? {
        required: 'Address line 2 is required.',
        validate: (value: string) =>
          validatePOBoxAddressLine2(value, !validatePOBox),
      }
    : {
        validate: (value: string) =>
          validatePOBoxAddressLine2(value, !validatePOBox),
      }

  return (
    <>
      <div
        className={`flex flex-col w-full${
          errors.address_line2 ? ' field-with-errors' : ''
        }`}
      >
        <label
          htmlFor='address_line2'
          className={`font-medium text-ds-text-secondary mb-2${
            errors.address_line1 ? ' text-red-800' : ''
          }`}
        >
          Street address
        </label>
        <div
          className={`address-fields${
            errors.address_line1 ? ' field-with-errors' : ''
          }`}
          data-testid='address_line1'
          ref={addressLine1Ref}
          onBlur={() => {
            // Since addressLine1Ref is not an input element,
            // we have to explicitly set the value from address_line1 form value
            // This case is to accommodate for any edits made outside of mapbox search
            if (addressLine1Ref.current) {
              const inputElement =
                addressLine1Ref.current.querySelector('input')
              if (inputElement) {
                setValue(
                  'address_line1' as Path<T>,
                  inputElement.value as PathValue<
                    AddressFieldsType,
                    Path<AddressFieldsType>
                  >
                )
              }
            }
          }}
        />
        <input
          id='address_line2'
          disabled={disabled}
          type='text'
          className='input-default mt-2'
          {...register('address_line2' as Path<T>, address2Validation)}
          autoComplete='address-line2'
        />
        {(errors.address_line1 || errors.address_line2) && (
          <span className='text-xs text-red-800 mt-2'>
            {(errors.address_line1 as FieldError | undefined)?.message}
            {errors.address_line1 && errors.address_line2 && <br />}
            {(errors.address_line2 as FieldError | undefined)?.message}
          </span>
        )}
      </div>

      <div className='flex gap-4 justify-between'>
        <FormTextField<AddressFieldsType>
          disabled={disabled}
          error={errors.city as FieldError}
          fieldId='city'
          fieldLabel='City'
          register={register as UseFormRegister<AddressFieldsType>}
          validationRule={
            requiredFields?.includes('city')
              ? {
                  required: 'City is required.',
                }
              : undefined
          }
        />
        <StateDropdown<AddressFieldsType>
          disabled={disabled}
          register={register as UseFormRegister<AddressFieldsType>}
          required={requiredFields?.includes('state')}
          error={errors.state as FieldError}
        />
      </div>

      <div className='flex gap-4 justify-between'>
        <FormTextField<AddressFieldsType>
          disabled={disabled}
          error={errors.zip as FieldError}
          fieldId='zip'
          fieldLabel='Zipcode'
          register={register as UseFormRegister<AddressFieldsType>}
          validationRule={
            requiredFields?.includes('zip')
              ? {
                  required: 'Zipcode is required.',
                }
              : undefined
          }
        />
        {extraField ?? <div className='flex flex-col w-full' />}
      </div>
    </>
  )
}
